import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ClubService } from '../../../services/club.service';
import { ImageUploadService } from '../../../services/image-upload.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { Club } from '../../../models/data.models';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';

@Component({
  selector: 'app-admin-clubs',
  standalone: true,
  imports: [CommonModule, TranslatePipe, PageHeaderComponent],
  templateUrl: './admin-clubs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminClubsComponent {
  private clubService = inject(ClubService);
  private imageUpload = inject(ImageUploadService);

  clubsByRegion = signal<Map<string, Club[]>>(new Map());
  isLoading = signal(true);
  uploadingClubId = signal<string | null>(null);
  uploadError = signal<string | null>(null);

  constructor() {
    this.loadClubs();
  }

  async loadClubs() {
    this.isLoading.set(true);
    try {
      const clubs = await this.clubService.getClubs();
      const map = new Map<string, Club[]>();

      clubs.forEach(club => {
        if (!map.has(club.region)) {
          map.set(club.region, []);
        }
        map.get(club.region)!.push(club);
      });

      this.clubsByRegion.set(map);
    } catch (err) {
      console.error(err);
    } finally {
      this.isLoading.set(false);
    }
  }

  asArray(map: Map<string, Club[]>): { key: string, value: Club[] }[] {
    return Array.from(map, ([key, value]) => ({ key, value }));
  }

  async onLogoSelected(event: Event, club: Club) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.uploadingClubId.set(club.id);
    this.uploadError.set(null);
    try {
      const logoUrl = await this.imageUpload.uploadClubLogo(club.id, file);
      await this.clubService.updateClubLogo(club.id, logoUrl);
      void this.imageUpload.removeStoredImage(club.logoUrl);
      await this.loadClubs();
    } catch (err) {
      console.error(err);
      this.uploadError.set(club.name);
    } finally {
      this.uploadingClubId.set(null);
      input.value = '';
    }
  }
}
